import React from "react";
import { Plus, Trash2, GripVertical, ExternalLink } from "lucide-react";
import { gbp } from "../../lib/apiClient";
import { Section } from "./layout";
import { useRowDrag } from "./useRowDrag";
import { numVal, parseNum, tmpId } from "./utils";

/**
 * HotelTable — overnight stays for engineers when one-way travel time meets
 * the configured threshold (default 2hrs).
 *
 * Mirrors `AccessHireTable` visually: collapsible Section wrapper, action
 * buttons (Required toggle + Add hotel) in the header.
 *
 * Columns: drag · Postcode · Booking link · Rooms · Nights · Cost / night ·
 *          Markup % · Markup £ · Line Total · delete
 *
 * Row line total = rooms × nights × cost_per_night × (1 + markup%).
 * Rooms defaults to `engineers` (1 room per engineer) but is editable.
 *
 * The auto "Added costs" row (engineer bonus + food + per-room contingency)
 * is non-editable — collapsed to a single Line Total cell (no breakdown
 * text). Per-room contingency now scales with the sum of rooms across rows.
 */
export function HotelTable({
  hotel = {},
  engineers = 1,
  costSettings = {},
  onChange,
  previewItems,
  previewAddedCosts,
  previewTotal = 0,
  storageKey,
}) {
  const required = !!hotel.required;
  const items = hotel.items || [];

  // Per Jun 2026 spec, the auto Added Costs is a flat per-engineer-per-night
  // figure (bundles room + food + bonus + contingency). Default £250.
  const perEngPerNight = Number(costSettings.hotel_per_engineer_per_night ?? 250);

  // Per Jun 2026 spec, rooms always equal engineers (1:1). The Rooms cell
  // is read-only and shows the live engineer count; any prior per-row value
  // is ignored so the figure stays in sync as engineers changes mid-build.
  const roomsFor = () => engineers;

  const setRequired = (val) => {
    if (val && (hotel.items || []).length === 0) {
      // Auto-seed a single hotel row prefilled with the configured per-eng-per-night
      // figure so the estimator doesn't have to type the cost manually. Rooms is
      // implicit (= engineers) and markup defaults to 0 — the £250 is the all-in
      // figure (food + bonus + contingency bundled), not a marked-up supplier cost.
      onChange({
        ...hotel,
        required: true,
        items: [{
          id: tmpId(),
          postcode: "",
          booking_url: "",
          nights: 1,
          cost_per_night: perEngPerNight,
          markup_percent: 0,
        }],
      });
      return;
    }
    onChange({ ...hotel, required: !!val });
  };
  const setItems = (next) => onChange({ ...hotel, items: next });

  const addRow = () => setItems([
    ...items,
    { id: tmpId(), postcode: "", booking_url: "", nights: 1, cost_per_night: perEngPerNight, markup_percent: 0 },
  ]);
  const updateRow = (idx, patch) => setItems(items.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));

  const drag = useRowDrag(items, setItems, "hotel-row");

  const lineFor = (idx, ln) => {
    const fromPreview = previewItems?.[idx];
    if (fromPreview && fromPreview.line_total != null) return fromPreview;
    const rooms = roomsFor(ln);
    const nights = Number(ln.nights || 0);
    const cost = Number(ln.cost_per_night || 0);
    const mp = Number(ln.markup_percent || 0) / 100;
    const sub = rooms * nights * cost;
    return { subtotal_raw: sub, markup_value: sub * mp, line_total: sub * (1 + mp) };
  };

  // Auto Added Costs is now baked into each hotel line (cost_per_night defaults
  // to the configured £250/eng/night). No separate "Added costs (auto)" row.
  const totalNights = items.reduce((s, r) => s + (Number(r.nights || 0)), 0);
  const totalRooms = items.reduce((s, r) => s + roomsFor(r), 0);

  return (
    <Section
      title="Hotel"
      subtitle="Overnight stays for engineers — booking links stay full-length and copyable in the PDF export."
      collapsible
      defaultOpen={false}
      storageKey={storageKey}
      action={
        <div className="flex items-center gap-3" data-testid="hotel-section-actions">
          <label
            className="flex items-center gap-2 select-none cursor-pointer"
            onClick={(e) => e.stopPropagation()}
            data-testid="hotel-required-toggle"
          >
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${required ? "text-emerald-700" : "text-slate-500"}`}>
              {required ? "Required" : "Not required"}
            </span>
            <span className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors ${required ? "bg-emerald-500" : "bg-slate-300"}`}>
              <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} className="sr-only" />
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${required ? "translate-x-[18px]" : "translate-x-0.5"}`} />
            </span>
          </label>
          <button
            onClick={(e) => { e.stopPropagation(); addRow(); }}
            disabled={!required}
            className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 hover:text-slate-900 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="hotel-add-row"
            title={required ? "Add a hotel row" : "Toggle Required to add hotels"}
          >
            <Plus size={14} /> Add hotel
          </button>
        </div>
      }
    >
      <div className={required ? "" : "opacity-50 pointer-events-none"} data-testid="hotel-section">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="w-full text-sm table-fixed min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th style={{ width: 28 }} className="px-1 py-2" />
              {[
                { l: "Postcode", w: "12%" },
                { l: "Booking Information", w: "25%" },
                { l: "Rooms", w: "6%", right: true },
                { l: "Nights", w: "7%", right: true },
                { l: "Cost / night", w: "11%", right: true },
                { l: "Markup %", w: "8%", right: true },
                { l: "Markup £", w: "9%", right: true },
                { l: "Line Total", w: "11%", right: true },
                { l: "", w: "36px" },
              ].map((h) => (
                <th
                  key={h.l || "actions"}
                  style={{ width: h.w }}
                  className={`px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600 ${h.right ? "text-right" : "text-left"}`}
                >{h.l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-400 text-sm">
                  No hotel rows yet.
                </td>
              </tr>
            )}
            {items.map((ln, idx) => {
              const calc = lineFor(idx, ln);
              const rooms = roomsFor(ln);
              return (
                <tr key={ln.id || idx} className="border-b border-slate-200" {...drag.dragProps(idx)}>
                  <td className="px-1 py-1 align-middle text-slate-400 hover:text-slate-700" {...drag.handleProps(idx)}>
                    <GripVertical size={14} />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      className="qb-input h-9 w-full font-mono"
                      value={ln.postcode || ""}
                      onChange={(e) => updateRow(idx, { postcode: e.target.value.toUpperCase() })}
                      placeholder="PL1 2AB"
                      data-testid={`hotel-postcode-${idx}`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <div className="flex items-center gap-1">
                      <input
                        className="qb-input h-9 w-full font-mono text-xs"
                        value={ln.booking_url || ""}
                        onChange={(e) => updateRow(idx, { booking_url: e.target.value })}
                        placeholder="https://booking.com/…"
                        data-testid={`hotel-url-${idx}`}
                      />
                      {(ln.booking_url || "").startsWith("http") && (
                        <a
                          href={ln.booking_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-slate-400 hover:text-brand-blue shrink-0"
                          title="Open in new tab"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-1 py-1">
                    <div
                      className="qb-input h-9 w-full font-mono text-right flex items-center justify-end bg-slate-100 text-slate-600 cursor-not-allowed"
                      data-testid={`hotel-rooms-${idx}`}
                      title="Rooms always match engineers (1 per engineer). Change the engineer count above to update."
                    >
                      {rooms}
                    </div>
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number" min="0"
                      value={numVal(ln.nights)}
                      onChange={(e) => updateRow(idx, { nights: parseNum(e.target.value) })}
                      className="qb-input h-9 w-full font-mono text-right"
                      data-testid={`hotel-nights-${idx}`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number" min="0" step="0.01"
                      value={numVal(ln.cost_per_night)}
                      onChange={(e) => updateRow(idx, { cost_per_night: parseNum(e.target.value) })}
                      className="qb-input h-9 w-full font-mono text-right"
                      data-testid={`hotel-cost-${idx}`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number" min="0" step="0.1"
                      value={numVal(ln.markup_percent)}
                      onChange={(e) => updateRow(idx, { markup_percent: parseNum(e.target.value) })}
                      className="qb-input h-9 w-full font-mono text-right"
                      data-testid={`hotel-markup-${idx}`}
                    />
                  </td>
                  <td className="px-2 py-1 font-mono text-right whitespace-nowrap text-slate-500">
                    {gbp(calc.markup_value || 0)}
                  </td>
                  <td
                    className="px-2 py-1 font-mono text-right whitespace-nowrap font-semibold"
                    data-testid={`hotel-line-total-${idx}`}
                  >
                    {gbp(calc.line_total || 0)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      onClick={() => removeRow(idx)}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`hotel-remove-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {/* Auto Added Costs is now baked into each hotel row's
                cost_per_night (£250 default). No separate "Added costs"
                row — keeps the table tidy. */}
          </tbody>
        </table>
        </div>

        {required && (items.length > 0 || totalNights > 0) && (
          <div className="mt-3 flex items-center justify-between text-sm bg-slate-50 rounded-lg px-4 py-2">
            <span className="text-slate-600">
              Hotel total <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-2">
                Engineers: <strong data-testid="hotel-engineers-readout">{engineers}</strong>
                <span className="mx-1.5">·</span>
                Rooms: <strong data-testid="hotel-rooms-readout">{totalRooms}</strong>
              </span>
            </span>
            <span className="font-mono font-semibold text-brand-blue" data-testid="hotel-section-total">
              {gbp(previewTotal || 0)}
            </span>
          </div>
        )}
      </div>
    </Section>
  );
}
